import {
  cacheProfileImage,
  clearCachedProfileImage,
  getCachedProfileImage,
} from "@/helpers/profile.helper";
import { RESPONSE_MESSAGES } from "constants/components";
import { responseMessage } from "helpers/errorHandling.helper";
import { useCallback, useEffect, useState } from "react";
import { useProfileImageStore } from "@/stores/profileImageStore";
import {
  removeProfilePictureV2,
  uploadProfilePictureV2,
} from "services/profile.services";
import { useProfileDetails } from "@/hooks/dataHooks/useProfileDetails";
import useAuth from "hooks/useAuth";

// ID HUB typically takes time to process uploads before the signed URL is ready
const ID_HUB_PROCESSING_DELAY_MS = 10000;

type ImageValidationResult = {
  valid: boolean;
  error?: string;
};

type UseProfileImageType = {
  profileImageUrl: string | null;
  isProfileImageLoading: boolean;
  isImageCaching: boolean;
  clearCache: () => Promise<void>;
  setCachedImage: (imagePath?: string, accountId?: string) => Promise<void>;
  // Upload operations
  isUploading: boolean;
  uploadImage: (file: File, onSuccess?: () => void) => Promise<boolean>;
  removeImage: (onSuccess?: () => void) => Promise<boolean>;
  validateImageFile: (file: File) => ImageValidationResult;
};

/**
 * Custom hook to manage profile image caching
 * - Uses Recoil atom for shared state across all components (single blob URL)
 * - Loads cached image from IndexedDB if available
 * - Only caches to IndexedDB when it's empty
 */

const useProfileImage = (): UseProfileImageType => {
  const { profileImageUrl, setProfileImageUrl } = useProfileImageStore();
  const [isImageCaching, setIsImageCaching] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const { isAuthenticated } = useAuth();
  const {
    data: profileData,
    isLoading: isProfileLoading,
    refetch,
  } = useProfileDetails(isAuthenticated);

  // Track the current blob URL for cleanup
  // const currentBlobUrlRef = useRef<string | null>(null);

  // Combine all loading states for seamless loading from upload to image display
  const isProfileImageLoading =
    isUploading || isProfileLoading || isImageCaching;

  // Load cached image from IndexedDB or cache from profile data
  useEffect(() => {
    let isCancelled = false;

    const loadOrCacheImage = async () => {
      setIsImageCaching(true);

      // If already have a URL in Recoil, don't create a new one
      if (profileImageUrl) {
        setIsImageCaching(false);
        return;
      }

      if (!profileData?.accountId || !profileData?.image_path) {
        setIsImageCaching(false);
        return;
      }

      try {
        // Check IndexedDB first
        const cached = await getCachedProfileImage();

        // Check if effect was cancelled during async operation
        if (isCancelled) {
          // Revoke the blob URL if we got one but effect was cancelled
          if (cached) URL.revokeObjectURL(cached);
          return;
        }

        if (cached) {
          // Found in IndexedDB, use it (won't re-cache)
          // currentBlobUrlRef.current = cached;
          setProfileImageUrl(cached);
          setIsImageCaching(false);
          return;
        }

        // Not in IndexedDB, cache from profile data to IndexedDB
        const newCachedUrl = await cacheProfileImage(
          profileData?.accountId,
          profileData?.image_path,
        );

        // Check again if effect was cancelled
        if (isCancelled) {
          if (newCachedUrl) URL.revokeObjectURL(newCachedUrl);
          return;
        }

        if (newCachedUrl) {
          // currentBlobUrlRef.current = newCachedUrl;
          setProfileImageUrl(newCachedUrl);
        }
      } finally {
        if (!isCancelled) {
          setIsImageCaching(false);
        }
      }
    };

    if (isAuthenticated && profileData?.image_path) {
      loadOrCacheImage();
    }

    // Cleanup function to handle cancellation and memory cleanup
    return () => {
      isCancelled = true;
    };
  }, [
    isAuthenticated,
    profileData?.image_path,
    profileData?.accountId,
    // profileImageUrl,
    setProfileImageUrl,
  ]);

  // Cleanup blob URLs on unmount to prevent memory leaks
  // useEffect(() => {
  //   return () => {
  //     if (
  //       currentBlobUrlRef.current &&
  //       currentBlobUrlRef.current.startsWith("blob:")
  //     ) {
  //       URL.revokeObjectURL(currentBlobUrlRef.current);
  //     }
  //   };
  // }, []);

  const setCachedImage = useCallback(
    async (imagePath?: string, accountId?: string) => {
      const url = imagePath || profileData?.image_path;
      const userId = accountId || profileData?.accountId;
      if (!userId || !url) return;
      const newCachedUrl = await cacheProfileImage(userId, url);
      if (newCachedUrl) {
        setProfileImageUrl(newCachedUrl);
      }
    },
    [profileData?.image_path, profileData?.accountId, setProfileImageUrl],
  );

  /**
   * Clear the cached profile image from IndexedDB and reset state
   */
  const clearCache = useCallback(async (): Promise<void> => {
    await clearCachedProfileImage();
    setProfileImageUrl(null);
  }, [setProfileImageUrl]);

  // Clear IndexedDB cache on browser refresh/reload
  // This ensures fresh image is cached from latest profile data on next load
  useEffect(() => {
    const handleBeforeUnload = () => {
      clearCachedProfileImage();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  /**
   * Validate image file type
   * @param file - File to validate
   * @returns Validation result with valid flag and optional error message
   */
  const validateImageFile = useCallback((file: File): ImageValidationResult => {
    const validTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!validTypes.includes(file.type)) {
      return {
        valid: false,
        error: RESPONSE_MESSAGES.UPLOAD_VALIDATION,
      };
    }
    return { valid: true };
  }, []);

  /**
   * Upload a new profile image
   * Handles: validation, cache clearing, API upload, and shows optimistic preview
   * After successful upload, immediately shows local file blob, then caches in background
   * @param file - Image file to upload
   * @param onSuccess - Optional callback after successful upload
   * @returns true if upload was successful, false otherwise
   */
  const uploadImage = useCallback(
    async (file: File, onSuccess?: () => void): Promise<boolean> => {
      // Validate file type
      const validation = validateImageFile(file);
      if (!validation.valid) {
        responseMessage(RESPONSE_MESSAGES.ERROR_CUSTOM, validation.error);
        return false;
      }

      setIsUploading(true);

      try {
        // Clear existing cached image before uploading new one
        await clearCachedProfileImage();

        // Upload to server
        const payload = { file };
        const res = await uploadProfilePictureV2(payload);

        if (res?.status === 200) {
          // Create local blob URL from the uploaded file for immediate display
          const localBlobUrl = URL.createObjectURL(file);
          // currentBlobUrlRef.current = localBlobUrl;
          setProfileImageUrl(localBlobUrl);
          setIsUploading(false);
          onSuccess?.();

          // Background: refetch profile data and cache the image after delay
          // Delay ensures the signed URL from server is ready
          // and even if user refresh the new use effect will work to cache the latest url
          setTimeout(async () => {
            try {
              const result = await refetch();
              if (result.data?.image_path && result.data?.accountId) {
                // Revoke the old local blob URL to free memory
                URL.revokeObjectURL(localBlobUrl);
                // Cache the image from signed URL to IndexedDB
                await setCachedImage(
                  result.data.image_path,
                  result.data.accountId,
                );
              }
            } catch (error) {
              console.error("Background caching failed:", error);
            }
          }, ID_HUB_PROCESSING_DELAY_MS);

          return true;
        } else {
          console.error("Upload failed with status:", res?.status);
          responseMessage(
            RESPONSE_MESSAGES.ERROR_CUSTOM,
            RESPONSE_MESSAGES.UPLOAD_FAILED,
          );
          setIsUploading(false);
          return false;
        }
      } catch (error) {
        console.error("Error uploading profile picture:", error);
        responseMessage(
          RESPONSE_MESSAGES.ERROR_CUSTOM,
          RESPONSE_MESSAGES.UPLOAD_FAILED,
        );
        setIsUploading(false);
        return false;
      }
    },
    [refetch, setCachedImage, setProfileImageUrl, validateImageFile],
  );

  /**
   * Remove the current profile image
   * Handles: API call, cache clearing, and triggers refetch
   * @param onSuccess - Optional callback after successful removal (refetch is already called internally)
   * @returns true if removal was successful, false otherwise
   */
  const removeImage = useCallback(
    async (onSuccess?: () => void): Promise<boolean> => {
      setIsUploading(true);

      try {
        const res = await removeProfilePictureV2();

        if (res?.status === 200) {
          // Clear cached image and blob URL ref
          // currentBlobUrlRef.current = null;
          // Wait for backend to process
          await new Promise((resolve) => setTimeout(resolve, 1000));
          // Trigger refetch
          await clearCache();
          await refetch();
          onSuccess?.();
          return true;
        } else {
          console.error("Remove failed with status:", res?.status);
          responseMessage(
            RESPONSE_MESSAGES.ERROR_CUSTOM,
            RESPONSE_MESSAGES.REMOVE_FAILED,
          );
          return false;
        }
      } catch (error) {
        console.error("Error removing profile picture:", error);
        responseMessage(
          RESPONSE_MESSAGES.ERROR_CUSTOM,
          RESPONSE_MESSAGES.REMOVE_FAILED,
        );
        return false;
      } finally {
        setIsUploading(false);
      }
    },
    [clearCache, refetch],
  );

  return {
    profileImageUrl,
    isProfileImageLoading,
    isImageCaching,
    clearCache,
    setCachedImage,
    // Upload operations
    isUploading,
    uploadImage,
    removeImage,
    validateImageFile,
  };
};

export default useProfileImage;
